/**
 * This file is meant for shared display components that you use throughout
 * your application.
 *
 * Components with a lot of logic, or those that are particularly complicated
 * should probably be put in their own file. This file is meant for the
 * re-usable, simple things used in a lot of different spots in your UI.
 */
import React, { useEffect } from 'react';
import styled from 'styled-components';
import WarningOutlined from '@mui/icons-material/WarningOutlined';
import { useLocation } from 'react-router-dom';

import { Box, Grid, Stack } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SouthEastIcon from '@mui/icons-material/SouthEast';
import Typography from '@mui/material/Typography';

interface GridViewProps {
    intent: string;
    targetGroup: string;
    relevantPowerDynamics: string;
    implication: string;
    targetGroupEmotionalReaction: string;
    targetGroupCognitiveReaction: string;
    offensiveness: string;
}

interface GridItemProps {
    type: string;
    item: string;
    icon1?: any;
    icon2?: any;
    size?: any
}

function BoxSx(props: GridItemProps) {
    const { type, item } = props;
  
    if (!item) {
      return null; // Render nothing if item is empty
    }
  
    // Define the color mapping
    const colorMapping: { [key: string]: string[] } = {
        'Intent': ['#d9eee8', '#1b9e77'],
        'Target Group': ['#f9e4d5', '#d95f02'],
        'Power Dynamics': ['#e8e7f2', '#7570b3'],
        'Implication': ['#fbdbec', '#e7298a'],
        'Emotional Reaction': ['#fbf1d5', '#e6ab02'],
        'Cognitive Reaction': ['#e5f0d9', '#66a61e'],
        'Offensiveness': ['#f0e8d9', '#a6761d'],
      };
  
    // Retrieve the color values based on the type
    const [backgroundColor, textColor] = colorMapping[type] || ['#f0f0f0', '#333'];
  
    return (
      <Box
        sx={{
          width: '200px', // Set the desired width
          height: '200px', // Set the desired height
          overflow: 'auto', // Allow text overflow with scroll
          display: 'inline-flex',
          flexDirection: 'column',
          backgroundColor: backgroundColor,
          '&:hover': {
            backgroundColor: '#e0e0e0',
            opacity: [0.9, 0.8, 0.7],
            transition: 'background-color 0.3s ease'
          },
          padding: '0.5em',
          borderRadius: '0.4em',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
        }}
      >
       <Typography variant="subtitle1" fontWeight="bold" sx={{ color: textColor }}>
        {type}:
      </Typography>
        {item}
      </Box>
    );
  }

function GridItem(props: GridItemProps) {
    const isItemEmpty = !props.item;
    let xs = 12;
    let sm = 6;
    let md = 4;

    if (props.size) {
        xs = props.size || xs;
        sm = props.size || sm;
        md = props.size || md;
    }

    return (
        <Grid item xs={xs} sm={sm} md={md}>
        <Stack>
          <BoxSx type={props.type} item={props.item} />
          <Grid container spacing={1}>
            {props.icon1 && <div>{props.icon1}</div>}
            {props.icon2 && <div>{props.icon2}</div>}
          </Grid>
        </Stack>
      </Grid>
    );
  }

export function GridView(props: GridViewProps) {
    return (
        <>
            <Grid container spacing={2}>
                <GridItem item={props.intent} type='Intent'/>
                <GridItem item={props.targetGroup} type='Target Group'/>
                <GridItem item={props.relevantPowerDynamics} type='Power Dynamics' />
            </Grid>
            <Grid container spacing={2}>
                <GridItem item={props.implication} type='Implication'/>
                <GridItem item={props.targetGroupCognitiveReaction} type='Cognitive Reaction'/>
                <GridItem item={props.targetGroupEmotionalReaction} type='Emotional Reaction'/>
            </Grid>
            <Grid container spacing={2}>
                <GridItem item={props.offensiveness} type='Offensiveness' />
            </Grid>
        </>
    );
}

export const Error = ({ message }: { message: string }) => (
    <ErrorGrid>
        <WarningOutlined />
        {message}
    </ErrorGrid>
);

const TwoColumnGrid = styled.div`
    display: grid;
    grid-template-columns: auto max-content;
    grid-gap: ${({ theme }) => `${theme.spacing.xs}`};
    align-items: center;
`;

const ErrorGrid = styled(TwoColumnGrid)`
    color: ${({ theme }) => theme.palette.text.error.hex};
`;

export const ScrollToTopOnPageChange = () => {
    const location = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location]);
    return null;
};
